import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, BookOpen, Building2, CheckCircle2, ExternalLink, Handshake, ListChecks, MessageSquareQuote,
  Play, Search, Target,
} from "lucide-react";
import { apiGet } from "../../../Api/Client.js";
import { safeHttpUrl } from "../../../Api/safeHttpUrl.js";
import { stamp } from "./CallPlayer.jsx";
import { scoreColor, shortDate } from "./callMeta.js";

// Accounts — the companies a seller is working, and a brief for the next call.
//
// A rep plans their week by company ("I've got Acme at 3"), not by a list of
// dated calls. This tab is organised that way, and each company opens onto a
// pre-call brief assembled from the calls already on record:
//
//   Before your next call   up to five moves, each tied to what raised it
//   Risks                   named, never scored into one "deal health" number
//   What they told you      the buyer's own words, with the moment to replay
//   What you promised       so the next call opens by keeping it
//   Where the deal is       the house playbook's stage, gate and hidden failure
//
// Every line points at a moment in a real call or at the playbook. A brief with
// nothing to point at stays short -- see Backend/Products/revops/intelligence.py.

const muted = { color: "var(--c-500)" };
const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };

function ago(days) {
  if (days == null) return "";
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}

// A single-series sparkline: the de-emphasis hue, the latest call in the
// accent, the value itself printed beside it.
function Sparkline({ values = [] }) {
  if (values.length < 2) return null;
  const W = 64, H = 20;
  const x = (i) => 2 + (i / (values.length - 1)) * (W - 4);
  const y = (v) => 2 + (1 - v / 10) * (H - 4);
  const d = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const last = values[values.length - 1];
  return (
    <svg width={W} height={H} aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={d} fill="none" stroke="var(--c-400, #9aa3b2)" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={x(values.length - 1)} cy={y(last)} r="3" fill="var(--blue-600)" stroke="var(--surface)" strokeWidth="1.5" />
    </svg>
  );
}

function Evidence({ item, onOpenCall }) {
  if (!item || !item.call_id) return null;
  return (
    <button type="button" onClick={() => onOpenCall && onOpenCall(item.call_id, item.at_seconds ?? null)}
            style={{ border: "none", background: "transparent", padding: 0, cursor: onOpenCall ? "pointer" : "default",
                     fontFamily: "inherit", fontSize: 11, color: "var(--blue-600)", fontWeight: 600,
                     display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
      <Play size={10} /> {shortDate(item.call_date)}{item.at_seconds != null ? ` · ${stamp(item.at_seconds)}` : ""}
    </button>
  );
}

function PlaybookChip({ link }) {
  if (!link || (!link.stage && !link.substep)) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--c-700)",
                   background: "var(--c-100, rgba(120,120,120,.1))", borderRadius: 999, padding: "2px 8px" }}
          title={link.law ? `Law: ${link.law}` : undefined}>
      <BookOpen size={11} /> {link.playbook && link.playbook !== "Universal" ? `${link.playbook} · ` : ""}
      {link.stage}{link.stage && link.substep ? " → " : ""}{link.substep}
    </span>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: ".04em", color: "var(--c-600)", marginBottom: 6 }}>
        <Icon size={13} /> {title}{count != null ? <span style={{ ...muted, fontWeight: 400 }}>({count})</span> : null}
      </div>
      {children}
    </div>
  );
}

const KIND_TONE = {
  objection: "var(--red-600, #c0392b)", price: "var(--red-600, #c0392b)", disengagement: "var(--red-600, #c0392b)",
  buyer_question: "var(--blue-600)", concession: "var(--green-600)", commitment: "var(--green-600)",
  said: "var(--c-600)",
};

function Brief({ brief, onOpenCall }) {
  const c = brief.company;
  const last = brief.last_call;
  const stage = brief.stage_guidance;

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Building2 size={16} color="var(--blue-600)" />
            <strong style={{ fontSize: 17, color: "var(--c-900)" }}>{c.name}</strong>
            {safeHttpUrl(c.website) && (
              <a href={safeHttpUrl(c.website)} target="_blank" rel="noreferrer" style={{ color: "var(--blue-600)" }} title={safeHttpUrl(c.website)}>
                <ExternalLink size={13} />
              </a>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--c-700)", marginTop: 3 }}>
            {[c.industry, c.product, c.stage && `CRM: ${c.stage}`].filter(Boolean).join(" · ") || "Not linked to a CRM lead"}
            {c.next_follow_up_at ? ` · follow-up due ${shortDate(c.next_follow_up_at)}` : ""}
          </div>
        </div>
        <span style={{ fontSize: 12, color: "var(--c-700)" }}>
          {brief.calls.length} call{brief.calls.length === 1 ? "" : "s"} on record
        </span>
      </div>

      {last && (
        <div style={{ marginTop: 10, padding: "9px 11px", borderRadius: 10, background: "var(--c-100, rgba(120,120,120,.07))",
                      fontSize: 12.5, color: "var(--c-800)" }}>
          <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", marginRight: 6 }}>
            Last call
          </span>
          {shortDate(last.call_date)}
          {last.average != null && (
            <strong style={{ marginLeft: 6, color: scoreColor(last.average) }}>{last.average}/10</strong>
          )}
          {last.headline ? <span> · {last.headline}</span> : null}
        </div>
      )}

      <Section icon={Target} title="Before your next call" count={brief.moves.length || null}>
        {brief.moves.length === 0 ? (
          <p style={{ ...muted, fontSize: 12.5, margin: 0 }}>
            Nothing on record points to a specific move. That usually means the calls so far had no push-back,
            no open question and no promise left hanging.
          </p>
        ) : brief.moves.map((m, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "24px 1fr", gap: 8, padding: "8px 0",
                                borderTop: i ? "1px solid var(--c-200)" : "none" }}>
            <span style={{ width: 22, height: 22, borderRadius: 999, background: "rgba(80,120,255,.14)",
                           color: "var(--blue-600)", fontSize: 12, fontWeight: 700, display: "inline-flex",
                           alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 650, color: "var(--c-900)" }}>{m.title}</div>
              <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 2, lineHeight: 1.5 }}>{m.detail}</div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                <Evidence item={m.evidence} onOpenCall={onOpenCall} />
                <PlaybookChip link={m.playbook} />
                {m.playbook?.law && <span style={{ fontSize: 11, ...muted }}>“{m.playbook.law}”</span>}
              </div>
            </div>
          </div>
        ))}
      </Section>

      {brief.risks.length > 0 && (
        <Section icon={AlertTriangle} title="Risks" count={brief.risks.length}>
          {brief.risks.map((r) => (
            <div key={r.code} style={{ padding: "6px 0", borderTop: "1px solid var(--c-200)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <span style={{ fontSize: 12.5, fontWeight: 650, color: "var(--c-900)", display: "inline-flex",
                               alignItems: "center", gap: 5 }}>
                  <AlertTriangle size={12} color="var(--amber-600, #c98500)" /> {r.label}
                </span>
                <Evidence item={r} onOpenCall={onOpenCall} />
              </div>
              <div style={{ fontSize: 12, color: "var(--c-700)", marginTop: 1 }}>{r.detail}</div>
              {r.quote && <div style={{ fontSize: 12, color: "var(--c-800)", fontStyle: "italic", marginTop: 1 }}>“{r.quote}”</div>}
            </div>
          ))}
        </Section>
      )}

      {brief.buyer_said.length > 0 && (
        <Section icon={MessageSquareQuote} title="What they told you" count={brief.buyer_said.length}>
          {brief.buyer_said.map((b, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "96px 1fr", gap: 8, padding: "6px 0",
                                  borderTop: i ? "1px solid var(--c-200)" : "none", alignItems: "baseline" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                             color: KIND_TONE[b.kind] || "var(--c-600)" }}>{b.label}</span>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12.5, color: "var(--c-800)", fontStyle: "italic" }}>“{b.quote}”</span>{" "}
                <Evidence item={b} onOpenCall={onOpenCall} />
              </div>
            </div>
          ))}
        </Section>
      )}

      {brief.you_committed.length > 0 && (
        <Section icon={Handshake} title="Promised and agreed" count={brief.you_committed.length}>
          {brief.you_committed.map((p, i) => (
            <div key={i} style={{ padding: "5px 0", fontSize: 12.5, color: "var(--c-800)" }}>
              <span style={{ fontStyle: "italic" }}>“{p.quote}”</span>{" "}
              {p.kind === "next_step" && (
                <span style={{ fontSize: 11, ...muted }}>
                  {p.next_step === "hard" ? "(next step, date agreed) " : "(next step, no date) "}
                </span>
              )}
              <Evidence item={p} onOpenCall={onOpenCall} />
            </div>
          ))}
        </Section>
      )}

      {stage && (
        <Section icon={ListChecks} title="Where the deal is">
          <div style={{ fontSize: 12.5, color: "var(--c-800)", lineHeight: 1.5 }}>
            <strong>Stage {stage.number} · {stage.name}</strong>
            <span style={muted}> — {stage.playbook} playbook, from CRM stage “{stage.crm_stage}”</span>
          </div>
          {stage.exit_gate && (
            <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 3 }}>
              <span style={{ ...muted, fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", marginRight: 6 }}>
                To move on
              </span>{stage.exit_gate}
            </div>
          )}
          {stage.hidden_failure && (
            <div style={{ fontSize: 12.5, color: "var(--c-700)", marginTop: 6, padding: "7px 10px", borderRadius: 8,
                          borderLeft: "3px solid var(--amber-600, #c98500)", background: "var(--c-100, rgba(120,120,120,.06))" }}>
              <strong style={{ color: "var(--c-900)" }}>Hidden failure{stage.hidden_failure.name ? ` — ${stage.hidden_failure.name}` : ""}:</strong>{" "}
              {stage.hidden_failure.body}
              {stage.hidden_failure.shows_up && <div style={{ ...muted, fontSize: 11.5, marginTop: 2 }}>Shows up at: {stage.hidden_failure.shows_up}</div>}
            </div>
          )}
          {stage.checklist.length > 0 && (
            <ul style={{ margin: "8px 0 0", paddingLeft: 0, listStyle: "none" }}>
              {stage.checklist.map((item, i) => (
                <li key={i} style={{ display: "flex", gap: 6, fontSize: 12.5, color: "var(--c-700)", padding: "2px 0" }}>
                  <CheckCircle2 size={13} style={{ flexShrink: 0, marginTop: 2 }} color="var(--c-500)" /> {item}
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      {brief.low_here.length > 0 && (
        <Section icon={Target} title="Where you've struggled with them">
          {brief.low_here.map((l) => (
            <div key={l.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12.5,
                                      padding: "4px 0", color: "var(--c-800)" }}>
              <span>{l.label}</span>
              <span style={{ color: "var(--c-700)" }}>missed on {l.misses} of {l.calls} calls here · avg {l.average}</span>
            </div>
          ))}
        </Section>
      )}

      <Section icon={Building2} title="Calls with them" count={brief.calls.length}>
        {brief.calls.map((call) => (
          <button key={call.id} type="button" onClick={() => onOpenCall && onOpenCall(call.id)}
                  style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, width: "100%", textAlign: "left",
                           padding: "7px 0", border: "none", borderTop: "1px solid var(--c-200)",
                           background: "transparent", cursor: onOpenCall ? "pointer" : "default", fontFamily: "inherit" }}>
            <span style={{ minWidth: 0, fontSize: 12.5, color: "var(--c-800)" }}>
              {shortDate(call.call_date)} · {call.call_label}
              {call.employee_name ? <span style={muted}> · {call.employee_name}</span> : null}
              {call.headline && <span style={{ display: "block", fontSize: 11.5, ...muted, marginTop: 1 }}>{call.headline}</span>}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(call.scoreable ? call.average : null) }}>
              {call.scoreable ? (call.average ?? "—") : "not scored"}
            </span>
          </button>
        ))}
      </Section>
    </div>
  );
}

export default function AccountsPanel({ employeeId = null, isManager = false, onOpenCall,
                                        focusCompany = null, onFocusHandled }) {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefError, setBriefError] = useState("");
  const [wholeTeam, setWholeTeam] = useState(false);

  const scopeQuery = isManager && !wholeTeam && employeeId ? `employee=${employeeId}` : "";

  useEffect(() => {
    let cancelled = false;
    setAccounts(null);
    apiGet(`/RevOps/accounts/${scopeQuery ? `?${scopeQuery}` : ""}`)
      .then((payload) => !cancelled && setAccounts(payload?.accounts || []))
      .catch((err) => !cancelled && setError(err?.message || "Couldn't load accounts."));
    return () => { cancelled = true; };
  }, [scopeQuery]);

  // Arriving from a call card's "Prep for the next call": open that company.
  useEffect(() => {
    if (focusCompany) {
      setSelected(focusCompany);
      setQuery("");
      if (onFocusHandled) onFocusHandled();
    }
  }, [focusCompany]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected && accounts && accounts.length) setSelected(accounts[0].name);
  }, [accounts, selected]);

  useEffect(() => {
    if (!selected) { setBrief(null); return undefined; }
    let cancelled = false;
    // The company changed: drop the old brief so it is never shown under the new name.
    setBrief(null);
    setBriefError("");
    const params = new URLSearchParams({ company: selected });
    if (scopeQuery) params.set("employee", String(employeeId));
    apiGet(`/RevOps/accounts/brief/?${params}`)
      .then((payload) => !cancelled && setBrief(payload))
      .catch((err) => !cancelled && setBriefError(err?.message || "Couldn't load the brief."));
    return () => { cancelled = true; };
  }, [selected, scopeQuery, employeeId]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (accounts || []).filter((a) => !q || a.name.toLowerCase().includes(q));
  }, [accounts, query]);

  if (error) return <div className="Login-Error">{error}</div>;
  if (!accounts) return <p style={muted}>Loading…</p>;
  if (!accounts.length) {
    return (
      <div style={card}>
        <p style={{ ...muted, fontSize: 13, margin: 0 }}>
          No calls with a named company yet. A call lands here once the recorder knows who the meeting
          was with — the company on the calendar invite, or the CRM lead it was booked against.
        </p>
      </div>
    );
  }

  const selectedKey = (selected || "").toLowerCase();

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-start" }}>
      <div style={{ ...card, flex: "1 1 280px", maxWidth: 400, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
                         color: "var(--c-600)" }}>{isManager ? "Accounts" : "Your accounts"}</span>
          <span style={{ fontSize: 11, ...muted }}>{accounts.length} compan{accounts.length === 1 ? "y" : "ies"}</span>
        </div>
        {isManager && (
          <div style={{ display: "flex", gap: 3, marginTop: 8 }}>
            {[[false, "This rep"], [true, "Whole team"]].map(([val, label]) => (
              <button key={label} onClick={() => { setWholeTeam(val); setSelected(null); }} style={{
                border: "1px solid var(--c-200)", cursor: "pointer", fontSize: 11.5, padding: "4px 9px", borderRadius: 7,
                background: wholeTeam === val ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
                fontWeight: wholeTeam === val ? 700 : 400, color: wholeTeam === val ? "var(--c-900)" : "var(--c-500)",
              }}>{label}</button>
            ))}
          </div>
        )}
        <span style={{ position: "relative", display: "flex", alignItems: "center", margin: "8px 0 6px" }}>
          <Search size={11} style={{ position: "absolute", left: 8, color: "var(--c-500)", pointerEvents: "none" }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a company"
                 aria-label="Find a company"
                 style={{ width: "100%", padding: "6px 8px 6px 24px", fontSize: 12, borderRadius: 7,
                          border: "1px solid var(--c-200)", background: "var(--surface)", color: "var(--c-900)" }} />
        </span>
        <div style={{ maxHeight: 560, overflowY: "auto", margin: "0 -6px" }}>
          {visible.map((a) => {
            const active = a.name.toLowerCase() === selectedKey || a.key === selectedKey;
            return (
              <button key={a.key} type="button" onClick={() => setSelected(a.name)} aria-current={active ? "true" : undefined}
                      style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, width: "100%", textAlign: "left",
                               padding: "9px 10px 9px 12px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit",
                               border: "1px solid transparent",
                               borderLeft: `3px solid ${active ? "var(--blue-600)" : "transparent"}`,
                               background: active ? "rgba(80,120,255,.13)" : "transparent" }}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: active ? 700 : 600, color: "var(--c-900)",
                                 overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                  <span style={{ display: "block", fontSize: 11, ...muted, marginTop: 2 }}>
                    {a.calls} call{a.calls === 1 ? "" : "s"} · last {ago(a.days_since_last)}
                    {a.stage ? ` · ${a.stage}` : ""}
                  </span>
                  {isManager && wholeTeam && a.reps.length > 0 && (
                    <span style={{ display: "block", fontSize: 11, ...muted }}>{a.reps.join(", ")}</span>
                  )}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Sparkline values={a.trend} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: scoreColor(a.last_average),
                                 minWidth: 26, textAlign: "right" }}>{a.last_average ?? "—"}</span>
                </span>
              </button>
            );
          })}
          {visible.length === 0 && <p style={{ ...muted, fontSize: 12, margin: "8px 6px" }}>No company matches.</p>}
        </div>
      </div>

      <div style={{ flex: "3 1 440px", minWidth: 0 }}>
        {briefError && <div className="Login-Error">{briefError}</div>}
        {!briefError && !brief && <p style={muted}>Loading the brief…</p>}
        {!briefError && brief && <Brief brief={brief} onOpenCall={onOpenCall} />}
      </div>
    </div>
  );
}
